import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar as CalendarIcon, ChevronDown, Check,
  ArrowRight, FileText, Sparkles, Mail,
  AlertTriangle, Hand, Bell, ShieldCheck,
} from 'lucide-react';
import { Calendar as CalendarComp } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, subMonths, differenceInDays } from 'date-fns';
import { useLanguage } from '@/lib/i18n';
import { buildSampleTrips, buildSummary } from '@/lib/sampleTrips';

/**
 * TollTripStatusChecker — honest demo of the JTR report-pulling
 * experience.
 *
 * Visitors pick a date range ("From" + "To" — single day if both are
 * the same) and see a SAMPLE styled preview of what a JTR daily email
 * would look like, plus a quick fact-card about MEISAI reflection
 * timing for the chosen range.
 *
 * Honest framing: every JTR customer gets these reports emailed
 * automatically — no logging in, no manual pull. So this widget is
 * purely a TASTE of the experience. No email capture, no account
 * lookup, no "notify me" promises we cannot fulfil for an anonymous
 * visitor who has no MEISAI link yet.
 *
 * Authenticated users do their real report lookups from the dashboard,
 * not from this marketing widget.
 */
const TollTripStatusChecker = ({ variant = 'compact' }) => {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const prefix = ja ? '/ja' : '/en';

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const fifteenMonthsAgo = useMemo(() => subMonths(today, 15), [today]);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // 'receipt' = simple receipt-style PDF (no times, fewer columns)
  // 'detailed' = full usage statement PDF (entry + exit times, vehicle,
  // OBU, ETC card last-4, discount). Default shows 'detailed' because
  // it carries more information density — visitors learn faster what
  // JTR's email actually contains.
  const [reportMode, setReportMode] = useState('detailed');

  // Headline fact about MEISAI timing for the chosen range — quick,
  // useful, and the only "answer" we can honestly give an anonymous
  // visitor based on the date math alone.
  const fact = useMemo(() => {
    if (!fromDate) return null;
    const from = new Date(fromDate);
    const to = toDate ? new Date(toDate) : from;
    const daysFromToday = differenceInDays(today, from);
    const sameDay = differenceInDays(to, from) === 0;
    if (daysFromToday < 0) {
      return ja
        ? '未来の日付です。走行後、ETC利用照会サービス（MEISAI）には通常4時間以内に反映されます。'
        : 'That date is in the future. After driving, MEISAI usually reflects the trip within ~4 hours.';
    }
    if (daysFromToday === 0) {
      return ja
        ? '本日のドライブはETC利用照会サービス（MEISAI）に通常4時間以内に反映されます。JTRが毎朝10:00（JST）までに前日分をメール配信します。'
        : 'Today\'s drives usually reflect on MEISAI within ~4 hours. JTR emails yesterday\'s totals before 10:00 JST every morning.';
    }
    if (daysFromToday <= 62) {
      return ja
        ? `${daysFromToday}日前${sameDay ? '' : '以降'}の走行はMEISAIに反映済みです（個人カード15か月／法人カード62日の閲覧範囲内）。JTRなら毎日自動でメール配信します。`
        : `Drives ${sameDay ? `from ${daysFromToday} days ago` : `in this range`} are reflected on MEISAI (within both the 15-month personal and 62-day corporate windows). JTR emails these every day automatically.`;
    }
    return ja
      ? `個人ETCカードでは過去15か月分が閲覧可能です。ETCコーポレートカードは62日のみ。JTRは毎日メール配信＋過去レポートも保管します。`
      : `Personal ETC cards: 15-month MEISAI window. Corporate cards: 62-day window only. JTR emails daily AND archives past reports for you.`;
  }, [fromDate, toDate, today, ja]);

  const handleReset = () => {
    setSubmitted(false);
    setFromDate(null);
    setToDate(null);
  };

  const rangeLabel = useMemo(() => {
    if (!fromDate) return '';
    const from = format(new Date(fromDate), ja ? 'yyyy年M月d日' : 'MMM d, yyyy');
    if (!toDate || differenceInDays(new Date(toDate), new Date(fromDate)) === 0) return from;
    const to = format(new Date(toDate), ja ? 'yyyy年M月d日' : 'MMM d, yyyy');
    return ja ? `${from} 〜 ${to}` : `${from} – ${to}`;
  }, [fromDate, toDate, ja]);

  // Renders a colorful date picker matching CustomReportsPage's pattern
  // (purple #9200C7 header strip + same day classNames).
  const DateField = ({ value, onChange, open, setOpen, label, headerLabel, testid, minDate, disabledFn }) => (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`w-full flex items-center justify-between gap-2 px-3.5 py-3 text-sm border-2 rounded-xl transition-all focus:outline-none focus:ring-4 ${value ? 'text-emerald-900 border-emerald-300 bg-emerald-50/50 focus:ring-emerald-500/15 focus:border-emerald-500' : 'text-slate-400 border-slate-300 bg-white hover:border-slate-400 focus:ring-purple-500/15 focus:border-purple-400'}`}
          data-testid={testid}
        >
          <span className="flex items-center gap-2.5 font-semibold min-w-0">
            <CalendarIcon className={`w-4 h-4 flex-shrink-0 ${value ? 'text-emerald-600' : 'text-slate-400'}`} strokeWidth={1.75} />
            <span className="text-[10px] font-bold uppercase tracking-wider opacity-60 flex-shrink-0">{label}</span>
            <span className="truncate">
              {value ? format(new Date(value), ja ? 'yyyy/M/d' : 'MMM d, yyyy') : (ja ? '選択…' : 'Pick…')}
            </span>
          </span>
          <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 ${value ? 'text-emerald-400' : 'text-slate-300'}`} strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 shadow-xl border border-slate-200 rounded-xl overflow-hidden bg-white"
        align="start"
        sideOffset={6}
        collisionPadding={12}
        avoidCollisions={true}
        style={{ zIndex: 9999 }}
      >
        <div className="bg-[#9200C7] px-4 py-2.5 text-center">
          <p className={`text-xs font-bold text-white/95 uppercase tracking-wider ${ja ? 'jtr-ja-text' : ''}`}>
            {headerLabel}
          </p>
        </div>
        <CalendarComp
          mode="single"
          selected={value || undefined}
          onSelect={(d) => { onChange(d || null); setOpen(false); }}
          fromDate={minDate || fifteenMonthsAgo}
          toDate={today}
          disabled={disabledFn || ((d) => d > today || d < fifteenMonthsAgo)}
          defaultMonth={value ? new Date(value) : today}
          initialFocus
          classNames={{
            months: "flex flex-col p-3",
            month: "space-y-3",
            caption: "flex justify-center pt-2 relative items-center px-8",
            caption_label: "text-sm font-bold text-slate-900",
            nav: "space-x-1 flex items-center",
            nav_button: "h-7 w-7 bg-transparent p-0 flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-100 hover:border-slate-300 transition-colors",
            nav_button_previous: "absolute left-2",
            nav_button_next: "absolute right-2",
            table: "w-full border-collapse",
            head_row: "flex mb-1",
            head_cell: "text-slate-400 rounded-md w-8 font-semibold text-[10px] uppercase tracking-wide text-center",
            row: "flex w-full",
            cell: "relative p-0 text-center text-sm [&:has([aria-selected])]:bg-purple-50 [&:has([aria-selected])]:rounded-md",
            day: "h-8 w-8 p-0 font-normal rounded-md hover:bg-purple-50 hover:text-[#9200C7] transition-colors text-sm",
            day_selected: "bg-[#9200C7] text-white hover:bg-[#7d00aa] hover:text-white focus:bg-[#9200C7] focus:text-white rounded-md font-bold",
            day_today: "border border-purple-300 text-[#9200C7] font-semibold",
            day_disabled: "text-slate-200 cursor-not-allowed hover:bg-transparent hover:text-slate-200",
            day_outside: "text-slate-300 opacity-50",
          }}
        />
      </PopoverContent>
    </Popover>
  );

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (fromDate) setSubmitted(true);
  };

  return (
    <div
      className={`w-full ${variant === 'compact' ? 'max-w-3xl' : 'max-w-4xl'} mx-auto rounded-3xl bg-white border-2 border-slate-200 shadow-xl overflow-hidden`}
      data-testid="toll-trip-status-checker"
    >
      {/* Header strip */}
      <div className="bg-gradient-to-r from-slate-900 via-[#1a0035] to-slate-900 px-6 py-5 sm:px-8 sm:py-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-300" strokeWidth={2.4} />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] sm:text-xs font-black uppercase tracking-[0.22em] text-emerald-300 mb-1">
              {ja ? 'サンプルレポート · 体験デモ' : 'Sample report · Live demo'}
            </p>
            <h2 className={`text-lg sm:text-xl lg:text-2xl font-black text-white leading-tight ${ja ? 'jtr-ja-text' : ''}`}>
              {ja
                ? 'ETC利用レポートを試しに引き出してみる'
                : 'Pull a sample ETC report'}
            </h2>
            <p className={`text-xs sm:text-sm text-slate-300 mt-1 leading-snug ${ja ? 'jtr-ja-text' : ''}`}>
              {ja
                ? '実際のJTRが毎日メール配信するレポートのプレビュー。お客様になれば、これが自動で毎日届きます。'
                : 'A preview of the daily ETC report JTR emails our customers. Once you sign up, these arrive automatically — no need to ever pull one manually.'}
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-6 sm:p-8">
        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="trip-checker-form">
            <div>
              <label className={`block text-sm font-bold text-slate-900 mb-2 ${ja ? 'jtr-ja-text' : ''}`}>
                {ja ? '対象期間' : 'Report period'}
                <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-rose-500">
                  {ja ? '必須' : 'required'}
                </span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DateField
                  value={fromDate}
                  onChange={(d) => {
                    setFromDate(d);
                    if (d && toDate && d > toDate) setToDate(d);
                  }}
                  open={fromOpen}
                  setOpen={setFromOpen}
                  label={ja ? '開始' : 'From'}
                  headerLabel={ja ? '開始日（過去15か月以内）' : 'From date (last 15 months)'}
                  testid="trip-checker-from"
                />
                <DateField
                  value={toDate}
                  onChange={setToDate}
                  open={toOpen}
                  setOpen={setToOpen}
                  label={ja ? '終了' : 'To'}
                  headerLabel={ja ? '終了日' : 'To date'}
                  testid="trip-checker-to"
                  minDate={fromDate || fifteenMonthsAgo}
                  disabledFn={(d) => d > today || d < (fromDate || fifteenMonthsAgo)}
                />
              </div>
              <p className={`mt-2 text-[11px] text-slate-500 leading-relaxed ${ja ? 'jtr-ja-text' : ''}`}>
                {ja
                  ? '1日だけの場合は同じ日を選択してください。MEISAI閲覧範囲：個人ETCカード15か月／ETCコーポレートカード62日。'
                  : 'For a single day, pick the same date in both fields. MEISAI window: 15 months (personal) / 62 days (corporate).'}
              </p>
            </div>

            <button
              type="submit"
              disabled={!fromDate}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl text-white font-extrabold text-base sm:text-lg shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(rgb(92, 255, 92) 0%, rgb(0, 200, 0) 40%, rgb(0, 154, 0) 100%)' }}
              data-testid="trip-checker-submit"
            >
              {ja ? 'サンプルレポートを表示' : 'Show sample report'}
              <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
            </button>

            <p className={`text-[11px] text-slate-500 text-center leading-relaxed ${ja ? 'jtr-ja-text' : ''}`}>
              {ja
                ? '入力情報は保存しません。お客様になれば、これらのレポートが毎日自動でメール配信されます。'
                : 'Nothing is stored. Once you become a customer, these reports are emailed to you automatically every day.'}
            </p>
          </form>
        ) : (
          <div data-testid="trip-checker-result">
            {/* Quick fact about MEISAI timing for the chosen range */}
            <div className="flex items-start gap-3 p-4 rounded-2xl border-2 bg-emerald-50 border-emerald-200">
              <div className="shrink-0 w-9 h-9 rounded-full bg-white flex items-center justify-center border border-emerald-200">
                <Check className="w-5 h-5 text-emerald-600" strokeWidth={2.4} />
              </div>
              <div className="min-w-0">
                <p className={`text-base sm:text-lg font-black text-slate-900 leading-snug ${ja ? 'jtr-ja-text' : ''}`} data-testid="trip-checker-result-headline">
                  {rangeLabel}
                </p>
                <p className={`text-sm text-slate-700 mt-2 leading-relaxed ${ja ? 'jtr-ja-text' : ''}`}>
                  {fact}
                </p>
              </div>
            </div>

            {/* Sample PDF-style report preview. Visually mirrors the
                actual JTR daily PDF email that customers receive —
                matches the real MEISAI column order (date, time, entry
                IC → exit IC, vehicle class 車種, OBU last 4, ETC card
                last 4, base toll, discount, final toll). Clearly
                labeled SAMPLE so visitors never mistake it for live
                account data. Iter 391e: dynamic trip data via
                lib/sampleTrips.js + real PDF column structure. */}
            {(() => {
              const trips = buildSampleTrips(fromDate, toDate, ja);
              const summary = buildSummary(trips);
              const authorizedVehicleCount = Math.max(0, summary.distinctVehicles.length - summary.unauthorizedVehicles.length);
              // Precompute the smart-summary sentence so we never need
              // multi-branch fragments inside JSX children (which can
              // trip parsers).
              const parts = [];
              if (ja) {
                parts.push(`ご登録車両で ${summary.authorizedCount}回`);
                if (summary.unauthorizedCount > 0) parts.push(`未登録車両で ${summary.unauthorizedCount}回`);
                if (summary.manualTollCount > 0) parts.push(`手動精算（料金所手渡し）${summary.manualTollCount}回`);
              } else {
                parts.push(`${summary.authorizedCount} authorized trip${summary.authorizedCount === 1 ? '' : 's'}`);
                if (summary.unauthorizedCount > 0) parts.push(`${summary.unauthorizedCount} on unrecognised vehicle${summary.unauthorizedCount === 1 ? '' : 's'}`);
                if (summary.manualTollCount > 0) parts.push(`${summary.manualTollCount} manual toll-booth handoff${summary.manualTollCount === 1 ? '' : 's'}`);
              }
              const smartLine = ja
                ? `${parts.join('、')}の走行を検出しました。`
                : `${parts.join(', ')} detected this period.`;
              return (
                <div className="mt-5 rounded-2xl border-2 border-slate-200 bg-slate-100/60 overflow-hidden shadow-inner" data-testid="trip-checker-sample-email">
                  {/* ── Smart Audit Banner — surfaces the JTR-only
                      "advanced features" we add on top of raw MEISAI
                      data: authorized-vehicle match, unauthorized
                      vehicle detection, and manual toll-booth handoff
                      detection. This is the AI-style summary that
                      appears at the top of the customer's real daily
                      email. */}
                  <div className="bg-gradient-to-r from-indigo-50 via-violet-50 to-fuchsia-50 border-b-2 border-indigo-200 px-4 py-3" data-testid="trip-checker-smart-summary">
                    <div className="flex items-start gap-2.5">
                      <div className="shrink-0 w-8 h-8 rounded-full bg-white border border-indigo-200 flex items-center justify-center mt-0.5">
                        <Sparkles className="w-4 h-4 text-indigo-600" strokeWidth={2.4} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-[10px] font-black uppercase tracking-[0.18em] text-indigo-700 mb-1 ${ja ? 'jtr-ja-text' : ''}`}>
                          {ja ? 'スマート解析サマリー' : 'Smart audit summary'}
                        </p>
                        <p className={`text-[12px] sm:text-[13px] text-slate-800 leading-relaxed ${ja ? 'jtr-ja-text' : ''}`} data-testid="trip-checker-smart-summary-line">
                          {smartLine}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {summary.unauthorizedVehicles.length > 0 && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 border border-rose-200 text-rose-800 text-[10px] font-bold ${ja ? 'jtr-ja-text' : ''}`}>
                              <AlertTriangle className="w-3 h-3" strokeWidth={2.6} />
                              {ja ? `未認可: ${summary.unauthorizedVehicles.join('、')}` : `Unauthorized: ${summary.unauthorizedVehicles.join(', ')}`}
                            </span>
                          )}
                          {summary.totalFinal > 8000 && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-amber-800 text-[10px] font-bold ${ja ? 'jtr-ja-text' : ''}`}>
                              <Bell className="w-3 h-3" strokeWidth={2.6} />
                              {ja ? `スマートアラート: ¥${summary.totalFinal.toLocaleString()} 通常より多め` : `Smart alert: ¥${summary.totalFinal.toLocaleString()} above usual`}
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-[10px] font-bold ${ja ? 'jtr-ja-text' : ''}`}>
                            <ShieldCheck className="w-3 h-3" strokeWidth={2.6} />
                            {ja ? `登録車両 ${authorizedVehicleCount}台` : `${authorizedVehicleCount} registered vehicle${authorizedVehicleCount === 1 ? '' : 's'}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SAMPLE banner + mode toggle */}
                  <div className="bg-slate-900 px-4 py-3 flex flex-wrap items-center gap-2">
                    <FileText className="w-4 h-4 text-emerald-300" strokeWidth={2.5} />
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300">
                      {ja ? 'サンプルPDFプレビュー' : 'Sample PDF preview'}
                    </p>
                    {/* PDF mode toggle — JTR delivers 3 file formats:
                        a simple receipt PDF, a detailed usage-statement
                        PDF, and a CSV/Excel spreadsheet. */}
                    <div className="inline-flex rounded-lg bg-slate-800 border border-slate-700 overflow-hidden ml-1" role="tablist" data-testid="trip-checker-pdf-mode">
                      <button
                        type="button"
                        onClick={() => setReportMode('detailed')}
                        className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider transition-colors ${reportMode === 'detailed' ? 'bg-emerald-500 text-white' : 'text-slate-300 hover:text-white'}`}
                        data-testid="trip-checker-pdf-mode-detailed"
                        aria-pressed={reportMode === 'detailed'}
                      >
                        {ja ? '利用明細' : 'Detailed'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setReportMode('receipt')}
                        className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider transition-colors border-l border-slate-700 ${reportMode === 'receipt' ? 'bg-emerald-500 text-white' : 'text-slate-300 hover:text-white'}`}
                        data-testid="trip-checker-pdf-mode-receipt"
                        aria-pressed={reportMode === 'receipt'}
                      >
                        {ja ? '領収書' : 'Receipt'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setReportMode('excel')}
                        className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider transition-colors border-l border-slate-700 ${reportMode === 'excel' ? 'bg-emerald-500 text-white' : 'text-slate-300 hover:text-white'}`}
                        data-testid="trip-checker-pdf-mode-excel"
                        aria-pressed={reportMode === 'excel'}
                      >
                        {ja ? 'Excel/CSV' : 'Excel/CSV'}
                      </button>
                    </div>
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-slate-300">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                      {ja ? '実データではありません' : 'Not your real data'}
                    </span>
                  </div>

                  {/* "Paper" surface — mimics an A4 PDF page */}
                  <div className="bg-white m-3 sm:m-5 p-4 sm:p-6 rounded-md border border-slate-200 relative" style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.04), 0 6px 14px rgba(0,0,0,0.04)' }}>
                    {/* Diagonal SAMPLE watermark */}
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
                      <p className="text-[64px] sm:text-[88px] font-black text-slate-200/40 rotate-[-22deg] select-none tracking-widest">
                        {ja ? 'サンプル' : 'SAMPLE'}
                      </p>
                    </div>

                    <div className="relative">
                      {reportMode === 'receipt' ? (
                        /* ── Receipt PDF view — single-document
                            Japanese-style 領収書 with prominent total,
                            no per-trip detail. */
                        <div data-testid="trip-checker-sample-receipt">
                          <div className="text-center pb-4 mb-4 border-b-2 border-double border-slate-300">
                            <p className={`text-[10px] font-black uppercase tracking-[0.22em] text-slate-500 ${ja ? 'jtr-ja-text' : ''}`}>
                              Japan Toll Receipts
                            </p>
                            <h3 className={`text-2xl sm:text-3xl font-black text-slate-900 mt-1 ${ja ? 'jtr-ja-text' : ''}`} style={{ letterSpacing: ja ? '0.18em' : '0.04em' }}>
                              {ja ? '領　収　書' : 'RECEIPT'}
                            </h3>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-[11px] mb-4">
                            <div>
                              <p className={`font-bold uppercase tracking-wider text-slate-400 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? '発行日' : 'Issued'}
                              </p>
                              <p className="font-semibold text-slate-900 tabular-nums mt-0.5">
                                {format(new Date(), ja ? 'yyyy年M月d日' : 'MMM d, yyyy')}
                              </p>
                            </div>
                            <div>
                              <p className={`font-bold uppercase tracking-wider text-slate-400 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? '対象期間' : 'Period'}
                              </p>
                              <p className="font-semibold text-slate-900 mt-0.5">{rangeLabel}</p>
                            </div>
                            <div>
                              <p className={`font-bold uppercase tracking-wider text-slate-400 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? '宛名' : 'Bill to'}
                              </p>
                              <p className={`font-semibold text-slate-900 mt-0.5 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? '〇〇〇〇 様' : 'Customer Name'}
                              </p>
                            </div>
                            <div>
                              <p className={`font-bold uppercase tracking-wider text-slate-400 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? '走行件数' : 'Trip count'}
                              </p>
                              <p className="font-semibold text-slate-900 mt-0.5 tabular-nums">{summary.totalTrips}{ja ? '件' : ''}</p>
                            </div>
                          </div>

                          {/* Amount block — the visual centerpiece */}
                          <div className="bg-slate-50 border-y-4 border-double border-slate-400 py-5 px-4 my-4 text-center">
                            <p className={`text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 ${ja ? 'jtr-ja-text' : ''}`}>
                              {ja ? '金　額' : 'Amount'}
                            </p>
                            <p className="text-3xl sm:text-4xl font-black text-slate-900 mt-1 tabular-nums" style={{ letterSpacing: '0.04em' }}>
                              ¥{summary.totalFinal.toLocaleString()}
                            </p>
                            <p className={`text-[11px] text-slate-500 mt-2 ${ja ? 'jtr-ja-text' : ''}`}>
                              {ja
                                ? `（内訳: ${summary.distinctVehicles.length}台 / ${summary.distinctCards.length}枚、ETC割引 −¥${summary.totalDiscount.toLocaleString()}）`
                                : `(Across ${summary.distinctVehicles.length} vehicle(s) / ${summary.distinctCards.length} card(s), ETC discounts −¥${summary.totalDiscount.toLocaleString()})`}
                            </p>
                          </div>

                          <div className="text-center mb-4">
                            <p className={`text-[11px] text-slate-600 ${ja ? 'jtr-ja-text' : ''}`}>
                              {ja ? '但し、上記期間のETC通行料金として' : 'For ETC toll fees in the period above'}
                            </p>
                            <p className={`text-[11px] text-slate-600 mt-1 ${ja ? 'jtr-ja-text' : ''}`}>
                              {ja ? '上記正に領収いたしました' : 'Payment is hereby acknowledged'}
                            </p>
                          </div>

                          <div className="pt-3 border-t border-slate-200 grid grid-cols-2 gap-3 text-[10px] text-slate-500">
                            <div>
                              <p className={`font-bold ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? '発行者' : 'Issuer'}
                              </p>
                              <p className={`leading-snug mt-0.5 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? 'Japan Toll Receipts\nNoJo Enterprise, Co. Ltd.\n神奈川県横須賀市' : 'Japan Toll Receipts\nNoJo Enterprise, Co. Ltd.\nYokosuka, Kanagawa, Japan'}
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="inline-block border-2 border-rose-400 rounded-full px-3 py-2 text-rose-500 font-black" style={{ letterSpacing: '0.1em' }}>
                                {ja ? '印' : 'SEAL'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : reportMode === 'excel' ? (
                        /* ── Excel / CSV view — spreadsheet grid with
                            column letters + the real MEISAI columns. */
                        <div data-testid="trip-checker-sample-excel">
                          <div className="flex items-start justify-between border-b border-slate-200 pb-3 mb-3 gap-3">
                            <div>
                              <p className={`text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 ${ja ? 'jtr-ja-text' : ''}`}>
                                Japan Toll Receipts · CSV / Excel
                              </p>
                              <h3 className={`text-base sm:text-lg font-black text-slate-900 leading-tight mt-1 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? 'ETC利用明細スプレッドシート' : 'ETC Usage Spreadsheet'}
                              </h3>
                              <p className={`text-[11px] text-slate-500 mt-0.5 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? `期間：${rangeLabel}　|　${summary.totalTrips}行` : `Period: ${rangeLabel} | ${summary.totalTrips} rows`}
                              </p>
                            </div>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-black uppercase tracking-wider shrink-0">
                              .csv · .xlsx
                            </span>
                          </div>

                          <div className="overflow-auto max-h-[520px] rounded border border-slate-300 bg-slate-50">
                            <table className="w-full text-[10.5px] sm:text-[11px] font-mono border-collapse">
                              <thead>
                                {/* Excel-style column letter row */}
                                <tr className="bg-slate-200 text-slate-600">
                                  <th className="w-8 px-1 py-1 border border-slate-300 text-center font-bold"> </th>
                                  {['A','B','C','D','E','F','G','H','I'].map(L => (
                                    <th key={L} className="px-2 py-1 border border-slate-300 text-center font-bold">{L}</th>
                                  ))}
                                </tr>
                                {/* MEISAI CSV column headers row 1 */}
                                <tr className="bg-emerald-50 text-slate-800">
                                  <th className="px-1 py-1 border border-slate-300 text-center font-bold tabular-nums">1</th>
                                  <th className={`px-2 py-1 border border-slate-300 font-bold text-left ${ja ? 'jtr-ja-text' : ''}`}>{ja ? '利用日' : 'Date'}</th>
                                  <th className={`px-2 py-1 border border-slate-300 font-bold text-left ${ja ? 'jtr-ja-text' : ''}`}>{ja ? '時刻(自)' : 'Entry'}</th>
                                  <th className={`px-2 py-1 border border-slate-300 font-bold text-left ${ja ? 'jtr-ja-text' : ''}`}>{ja ? '時刻(至)' : 'Exit'}</th>
                                  <th className={`px-2 py-1 border border-slate-300 font-bold text-left ${ja ? 'jtr-ja-text' : ''}`}>{ja ? '利用IC(自)' : 'From IC'}</th>
                                  <th className={`px-2 py-1 border border-slate-300 font-bold text-left ${ja ? 'jtr-ja-text' : ''}`}>{ja ? '利用IC(至)' : 'To IC'}</th>
                                  <th className={`px-2 py-1 border border-slate-300 font-bold text-left ${ja ? 'jtr-ja-text' : ''}`}>{ja ? '車種' : 'Class'}</th>
                                  <th className={`px-2 py-1 border border-slate-300 font-bold text-left ${ja ? 'jtr-ja-text' : ''}`}>{ja ? 'ETCカード' : 'Card'}</th>
                                  <th className={`px-2 py-1 border border-slate-300 font-bold text-right tabular-nums ${ja ? 'jtr-ja-text' : ''}`}>{ja ? '通行料金' : 'Toll'}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {trips.map((t, i) => (
                                  <tr key={i} className={`hover:bg-emerald-50/40 ${!t.authorized ? 'bg-rose-50' : 'bg-white'}`}>
                                    <td className="px-1 py-1 border border-slate-300 bg-slate-100 text-center font-bold text-slate-600 tabular-nums">{i + 2}</td>
                                    <td className="px-2 py-1 border border-slate-300 tabular-nums whitespace-nowrap">{t.dateLabel}</td>
                                    <td className="px-2 py-1 border border-slate-300 tabular-nums">{t.entryTime}</td>
                                    <td className="px-2 py-1 border border-slate-300 tabular-nums">{t.exitTime}</td>
                                    <td className={`px-2 py-1 border border-slate-300 whitespace-nowrap ${ja ? 'jtr-ja-text' : ''}`}>{t.entryIc}</td>
                                    <td className={`px-2 py-1 border border-slate-300 whitespace-nowrap ${ja ? 'jtr-ja-text' : ''}`}>{t.exitIc}</td>
                                    <td className={`px-2 py-1 border border-slate-300 ${!t.authorized ? 'text-rose-700 font-bold' : ''} ${ja ? 'jtr-ja-text' : ''}`}>
                                      {t.vehicleClass}{!t.authorized ? (ja ? ' ⚑' : ' ⚑') : ''}
                                    </td>
                                    <td className="px-2 py-1 border border-slate-300 tabular-nums">
                                      {t.cardLast4 ? `···${t.cardLast4}` : (ja ? '手渡し' : 'Manual')}
                                    </td>
                                    <td className="px-2 py-1 border border-slate-300 text-right tabular-nums font-bold">¥{t.final.toLocaleString()}</td>
                                  </tr>
                                ))}
                                {/* Total row */}
                                <tr className="bg-emerald-100 font-bold text-slate-900">
                                  <td className="px-1 py-1 border border-slate-300 bg-emerald-200 text-center tabular-nums">{trips.length + 2}</td>
                                  <td colSpan={7} className={`px-2 py-1 border border-slate-300 text-right font-bold ${ja ? 'jtr-ja-text' : ''}`}>{ja ? '合計' : 'Total'}</td>
                                  <td className="px-2 py-1 border border-slate-300 text-right tabular-nums">¥{summary.totalFinal.toLocaleString()}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          <p className={`mt-3 text-[10px] text-slate-500 leading-relaxed ${ja ? 'jtr-ja-text' : ''}`}>
                            {ja
                              ? '※ 実際のCSVはcp932（Shift_JIS）でエンコードされ、freee / マネーフォワード / 弥生会計などの会計ソフトに直接インポート可能です。'
                              : 'Real CSV is cp932 (Shift_JIS) encoded, ready to import directly into freee / Money Forward / Yayoi accounting software.'}
                          </p>
                        </div>
                      ) : (
                        /* ── Detailed Usage Report PDF view (full table) */
                        <div data-testid="trip-checker-sample-detailed">
                          {/* Letterhead */}
                          <div className="flex items-start justify-between border-b border-slate-200 pb-3 mb-4 gap-3">
                            <div>
                              <p className={`text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 ${ja ? 'jtr-ja-text' : ''}`}>
                                Japan Toll Receipts
                              </p>
                              <h3 className={`text-base sm:text-lg font-black text-slate-900 leading-tight mt-1 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? 'ETC利用明細レポート' : 'ETC Detailed Usage Report'}
                              </h3>
                              <p className={`text-[11px] text-slate-500 mt-0.5 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? `対象期間：${rangeLabel}` : `Period: ${rangeLabel}`}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className={`text-[10px] font-bold uppercase tracking-wider text-slate-400 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? '発行' : 'Issued'}
                              </p>
                              <p className="text-[11px] font-semibold text-slate-700 tabular-nums">
                                {format(new Date(), ja ? 'yyyy/MM/dd HH:mm' : 'MMM d, yyyy HH:mm')}
                              </p>
                            </div>
                          </div>

                          {/* Detailed trip table */}
                          <div className="overflow-auto max-h-[520px]">
                            <table className="w-full text-[11px] sm:text-xs">
                              <thead>
                                <tr className="text-left">
                                  <th className={`py-2 pr-2 font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-300 ${ja ? 'jtr-ja-text' : ''}`}>
                                    {ja ? '日付・時刻' : 'Date · Time'}
                                  </th>
                                  <th className={`py-2 px-2 font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-300 ${ja ? 'jtr-ja-text' : ''}`}>
                                    {ja ? '走行区間（IC→IC）' : 'Route (IC → IC)'}
                                  </th>
                                  <th className={`py-2 px-2 font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-300 ${ja ? 'jtr-ja-text' : ''} hidden sm:table-cell`}>
                                    {ja ? '車種・車番' : 'Vehicle · OBU'}
                                  </th>
                                  <th className={`py-2 px-2 font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-300 ${ja ? 'jtr-ja-text' : ''} hidden md:table-cell`}>
                                    {ja ? 'ETCカード' : 'ETC card'}
                                  </th>
                                  <th className={`py-2 pl-2 text-right font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-300 tabular-nums ${ja ? 'jtr-ja-text' : ''}`}>
                                    {ja ? '通行料金' : 'Toll'}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {trips.map((t, i) => (
                                  <tr key={i} className={`border-b border-slate-100 last:border-b-0 ${!t.authorized ? 'bg-rose-50/40' : ''}`}>
                                    <td className={`py-2 pr-2 align-top ${ja ? 'jtr-ja-text' : ''}`}>
                                      <p className="font-semibold text-slate-900 tabular-nums">{t.dateLabel}</p>
                                      <p className="text-slate-500 tabular-nums">{t.entryTime} → {t.exitTime}</p>
                                    </td>
                                    <td className={`py-2 px-2 align-top ${ja ? 'jtr-ja-text' : ''}`}>
                                      <p className="text-slate-900 font-semibold leading-snug">{t.entryIc}</p>
                                      <p className="text-slate-500 leading-snug">→ {t.exitIc}</p>
                                      {/* Inline flag badges — surface the audit flags from the smart summary. */}
                                      {(!t.authorized || t.manualToll) && (
                                        <div className="mt-1 flex flex-wrap gap-1">
                                          {!t.authorized && (
                                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-rose-100 border border-rose-300 text-rose-800 text-[9px] font-black uppercase tracking-wider ${ja ? 'jtr-ja-text' : ''}`}>
                                              <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2.8} />
                                              {ja ? '未登録車両' : 'Unauthorized'}
                                            </span>
                                          )}
                                          {t.manualToll && (
                                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 text-[9px] font-black uppercase tracking-wider ${ja ? 'jtr-ja-text' : ''}`}>
                                              <Hand className="w-2.5 h-2.5" strokeWidth={2.8} />
                                              {ja ? '手渡し精算' : 'Manual toll'}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                    <td className={`py-2 px-2 align-top hidden sm:table-cell ${ja ? 'jtr-ja-text' : ''}`}>
                                      <p className="text-slate-900 font-semibold">{t.vehicleClass}</p>
                                      {t.manualToll ? (
                                        <p className="text-amber-700 font-bold text-[10px] mt-0.5">{ja ? '手渡し（車両情報なし）' : 'Manual — no vehicle data'}</p>
                                      ) : (
                                        <>
                                          <p className="text-slate-500 tabular-nums">···{t.obuLast4}</p>
                                          <p className={`text-[10px] mt-0.5 ${t.authorized ? 'text-slate-400' : 'text-rose-600 font-bold'}`}>{t.plate}</p>
                                        </>
                                      )}
                                    </td>
                                    <td className="py-2 px-2 align-top hidden md:table-cell tabular-nums">
                                      {t.cardLast4 ? (
                                        <p className="text-slate-900 font-mono">···{t.cardLast4}</p>
                                      ) : (
                                        <p className={`text-amber-700 font-bold text-[10px] ${ja ? 'jtr-ja-text' : ''}`}>{ja ? '手渡し' : 'Manual'}</p>
                                      )}
                                    </td>
                                    <td className="py-2 pl-2 align-top text-right tabular-nums">
                                      <p className="font-black text-slate-900">¥{t.final.toLocaleString()}</p>
                                      {t.discount > 0 && (
                                        <p className="text-[10px] text-emerald-600 font-bold">−¥{t.discount.toLocaleString()}</p>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Totals strip */}
                          <div className="mt-3 pt-3 border-t-2 border-slate-300 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
                            <div>
                              <p className={`font-bold uppercase tracking-wider text-slate-500 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? '走行件数' : 'Trips'}
                              </p>
                              <p className="font-black text-slate-900 text-base mt-0.5 tabular-nums">{summary.totalTrips}</p>
                            </div>
                            <div>
                              <p className={`font-bold uppercase tracking-wider text-slate-500 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? '車両' : 'Vehicles'}
                              </p>
                              <p className="font-black text-slate-900 text-base mt-0.5 tabular-nums">{summary.distinctVehicles.length}</p>
                            </div>
                            <div>
                              <p className={`font-bold uppercase tracking-wider text-slate-500 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? 'ETC割引' : 'Discounts'}
                              </p>
                              <p className="font-black text-emerald-700 text-base mt-0.5 tabular-nums">−¥{summary.totalDiscount.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className={`font-bold uppercase tracking-wider text-slate-500 ${ja ? 'jtr-ja-text' : ''}`}>
                                {ja ? '合計通行料金' : 'Total toll'}
                              </p>
                              <p className="font-black text-slate-900 text-base mt-0.5 tabular-nums">¥{summary.totalFinal.toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Format-readiness footer (all 3 modes) */}
                      <div className="mt-4 pt-3 border-t border-slate-200 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">
                          <FileText className="w-3 h-3" strokeWidth={2.6} />PDF
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-[10px] font-bold uppercase tracking-wider">
                          <FileText className="w-3 h-3" strokeWidth={2.6} />CSV / Excel
                        </span>
                        <span className={`text-[10px] font-semibold text-slate-500 ml-auto leading-tight ${ja ? 'jtr-ja-text' : ''}`}>
                          {ja ? '経費精算・インボイス制度・電子帳簿保存法 対応' : 'Expense-report · Qualified-invoice · e-Storage Act ready'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* CTAs — primary trial + secondary sample */}
            <div id="trip-checker-cta" className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link
                to={`${prefix}/free-trial`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl text-white font-extrabold text-sm sm:text-base shadow-lg transition-transform hover:scale-[1.02]"
                style={{ background: 'linear-gradient(rgb(92, 255, 92) 0%, rgb(0, 200, 0) 40%, rgb(0, 154, 0) 100%)' }}
                data-testid="trip-checker-cta-trial"
              >
                {ja ? '自動で毎日もらう（無料体験）' : 'Get this automatically · free trial'}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
              <Link
                to={`${prefix}/sample-outputs`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-white text-slate-900 font-extrabold text-sm sm:text-base border-2 border-slate-300 hover:border-slate-400 transition-colors"
                data-testid="trip-checker-cta-sample"
              >
                <FileText className="w-4 h-4" strokeWidth={2.5} />
                {ja ? '他のレポート例を見る' : 'See more sample reports'}
              </Link>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-[12px] text-slate-500">
              <button
                type="button"
                onClick={handleReset}
                className="font-bold hover:text-slate-900 underline-offset-2 hover:underline"
                data-testid="trip-checker-reset"
              >
                {ja ? '別の期間で試す' : 'Try another period'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TollTripStatusChecker;
